import { HintAnimation } from '../../portal-hints/hint-motion';
import { reliefFolder } from '../../portal-hints/animations/scene-kit';
import {
  abs,
  bar,
  bubble,
  C,
  checkbox,
  chip,
  glass,
  hline,
  icon,
  okChip,
  orbi,
  panel,
  pill,
  pressed,
  StoryScene,
  text,
  toggle,
} from './story-scene';

/**
 * Szenen des Pilot-Themas „Wissensbasis & Synchronisierung“
 * (features/prd/academy-content.md › 4). Szene 1 der ersten drei Lektionen
 * ist der Hinweis-Film; hier stehen die Szenen danach. Begriffe wie im
 * Portal (assets/i18n/de.json). Panel-Koordinaten ab (20,18) — der Zeiger
 * rechnet in Bühnenkoordinaten (+20/+18).
 */

const folderIcon = (open = false) =>
  reliefFolder(`<div style="position:absolute;inset:0;">${icon(open ? 'folderOpen' : 'folder', 20)}</div>`, '');

const fileRow = (y: number, ic: string, name: string, n: number) =>
  `<div data-r="row${n}" style="${abs(180, y, 404, 40, 'opacity:0;')}">` +
  `<div style="height:40px;display:flex;align-items:center;gap:12px;padding:0 12px;">${icon(ic)}` +
  `<span style="flex:1;font-size:14px;font-weight:500;color:${C.text};white-space:nowrap;">${name}</span></div>` +
  `<div data-r="track${n}">${bar(326, 18, 62, `fill${n}`)}</div>` +
  okChip(316, 8, 78, 'Bereit', `ready${n}`).replace('display:flex', 'display:flex;opacity:0') +
  `</div>`;

/** 3.1 · Szene 2 — Dateien hochladen, dann eine Webseite hinzufügen. */
const kbUpload = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html: panel(
      pressed(180, 24, 404, 44) +
        `<div style="${abs(188, 29, 34, 34)}">${folderIcon(true)}</div>` +
        text(232, 34, 200, 'Prüfungen', 15, 600) +
        // Reiter: aktive Schrift fett, die andere Nebentext — beim Wechsel überblendet.
        `<div data-r="tabFiles">${text(188, 82, 74, 'Dateien', 14, 600, C.text, 'text-align:center;')}</div>` +
        `<div data-r="tabFilesOff" style="opacity:0;">${text(188, 82, 74, 'Dateien', 14, 500, C.muted, 'text-align:center;')}</div>` +
        `<div data-r="tabWebOff">${text(262, 82, 94, 'Webseiten', 14, 500, C.muted, 'text-align:center;')}</div>` +
        `<div data-r="tabWeb" style="opacity:0;">${text(262, 82, 94, 'Webseiten', 14, 600, C.text, 'text-align:center;')}</div>` +
        text(356, 82, 62, 'Kurse', 14, 500, C.muted, 'text-align:center;') +
        hline(180, 112, 404) +
        `<div data-r="tabUnder" style="${abs(198, 110, 54, 3, `border-radius:2px;background:${C.action};`)}"></div>` +
        pill(420, 124, 164, 'Dateien hochladen', 'upload') +
        pill(420, 124, 164, 'Webseite hinzufügen', 'addWeb').replace('transform-origin', 'opacity:0;transform-origin') +
        fileRow(168, 'file', 'Prüfungsordnung.pdf', 1) +
        fileRow(210, 'file', 'Klausur_WS25.pdf', 2) +
        fileRow(252, 'globe', 'www.beispiel-hochschule.de', 3),
      1,
    ),
    tracks: [
      { r: 'row1', at: [2.2, 2.7], o: [0, 1], x: [40, 0] },
      { r: 'fill1', at: [2.6, 4.0], s: [0, 1] },
      { r: 'track1', at: [4.0, 4.3], o: [1, 0] },
      { r: 'ready1', at: [4.0, 4.3], o: [0, 1] },
      { r: 'row2', at: [3.0, 3.5], o: [0, 1], x: [40, 0] },
      { r: 'fill2', at: [3.4, 4.8], s: [0, 1] },
      { r: 'track2', at: [4.8, 5.1], o: [1, 0] },
      { r: 'ready2', at: [4.8, 5.1], o: [0, 1] },
      { r: 'tabUnder', at: [6.8, 7.2], x: [0, 74] },
      { r: 'tabFiles', at: [6.8, 7.1], o: [1, 0] },
      { r: 'tabFilesOff', at: [6.8, 7.1], o: [0, 1] },
      { r: 'tabWebOff', at: [6.8, 7.1], o: [1, 0] },
      { r: 'tabWeb', at: [6.8, 7.1], o: [0, 1] },
      { r: 'upload', at: [6.8, 7.1], o: [1, 0] },
      { r: 'addWeb', at: [6.8, 7.1], o: [0, 1] },
      { r: 'row3', at: [7.8, 8.3], o: [0, 1], x: [40, 0] },
      { r: 'fill3', at: [8.2, 9.4], s: [0, 1] },
      { r: 'track3', at: [9.4, 9.7], o: [1, 0] },
      { r: 'ready3', at: [9.4, 9.7], o: [0, 1] },
    ],
    cursor: {
      at: [0, 1.4, 2.3, 5.6, 6.6, 7.3, 8.6, 12],
      x: [380, 522, 522, 522, 329, 329, 470, 470],
      y: [250, 160, 160, 160, 108, 108, 300, 300],
      clicks: [2.0, 6.8],
    },
  });

/** 3.1 · Szene 3 — ein Ordner je Thema, später gezielt verknüpfbar. */
const kbFolders = (): HintAnimation =>
  new StoryScene({
    seconds: 10,
    still: 9,
    html: panel(
      text(186, 26, 120, 'Ordner', 12, 600, C.muted) +
        `<div data-r="plus" style="${abs(326, 20, 30, 30, `border-radius:999px;background:${C.action};display:flex;align-items:center;justify-content:center;`)}">${icon('plus', 16, '#fff', 2.2)}</div>` +
        pressed(180, 58, 180, 42, 'sel1') +
        `<div style="${abs(186, 62, 34, 34)}">${folderIcon()}</div>` +
        text(228, 68, 120, 'Prüfungen', 14, 600) +
        `<div data-r="f2" style="${abs(0, 0, 0, 0, 'opacity:0;')}">` +
        pressed(180, 104, 180, 42, 'sel2').replace('border-radius', 'opacity:0;border-radius') +
        `<div style="${abs(186, 108, 34, 34)}">${folderIcon()}</div>` +
        text(228, 114, 120, 'Skripte', 14, 600) +
        `</div>` +
        `<div style="${abs(372, 20, 2, 180, 'background:linear-gradient(to right, rgb(52 76 117 / .17) 0 1px, rgb(255 255 255 / .78) 1px 2px);')}"></div>` +
        `<div data-r="p1">${text(386, 64, 190, 'Prüfungsordnung.pdf', 13)}${text(386, 96, 190, 'Klausur_WS25.pdf', 13)}</div>` +
        `<div data-r="s1" style="opacity:0;">${text(386, 64, 190, 'Skript_Kapitel1.pdf', 13)}</div>` +
        `<div data-r="s2" style="opacity:0;">${text(386, 96, 190, 'Skript_Kapitel2.pdf', 13)}</div>` +
        hline(180, 214, 404) +
        `<div data-r="m1" style="opacity:0;">` +
        `<div style="${abs(190, 226, 26, 26)}">${icon('folder', 20)}</div>${text(220, 228, 120, 'Prüfungen', 13, 600)}` +
        `<div style="${abs(338, 230, 20, 20)}">${icon('chevron', 18, C.muted, 1.8, 'transform:rotate(-90deg);')}</div>` +
        orbi(372, 224, 26) +
        text(406, 228, 170, 'Klausur-Coach', 13, 600) +
        `</div>` +
        `<div data-r="m2" style="opacity:0;">` +
        `<div style="${abs(190, 266, 26, 26)}">${icon('folder', 20)}</div>${text(220, 268, 120, 'Skripte', 13, 600)}` +
        `<div style="${abs(338, 270, 20, 20)}">${icon('chevron', 18, C.muted, 1.8, 'transform:rotate(-90deg);')}</div>` +
        orbi(372, 264, 26) +
        text(406, 268, 170, 'Statistik-Tutor', 13, 600) +
        `</div>`,
      1,
    ),
    tracks: [
      { r: 'f2', at: [1.8, 2.3], o: [0, 1], y: [8, 0] },
      { r: 'sel1', at: [2.6, 2.9], o: [1, 0] },
      { r: 'sel2', at: [2.6, 2.9], o: [0, 1] },
      { r: 'p1', at: [2.6, 2.8], o: [1, 0] },
      { r: 's1', at: [3.0, 3.4], o: [0, 1] },
      { r: 's2', at: [3.4, 3.8], o: [0, 1] },
      { r: 'm1', at: [5.6, 6.1], o: [0, 1], y: [6, 0] },
      { r: 'm2', at: [6.6, 7.1], o: [0, 1], y: [6, 0] },
    ],
    cursor: {
      at: [0, 1.2, 1.7, 2.3, 4.5, 10],
      x: [300, 361, 361, 290, 290, 290],
      y: [260, 53, 53, 140, 140, 140],
      clicks: [1.5, 2.5],
    },
  });

/** 3.2 · Szene 2 — ohne Verknüpfung keine Quelle; Häkchen setzen, gleiche Frage, Antwort mit Quelle. */
const linkPreview = (): HintAnimation =>
  new StoryScene({
    seconds: 14,
    still: 12.5,
    html: panel(
      text(184, 24, 180, 'Wissensbasis – Verknüpfung', 12, 600, C.muted) +
        checkbox(188, 64, 'cb1') +
        `<div style="${abs(214, 58, 30, 30)}">${icon('folder', 20)}</div>${text(242, 62, 110, 'Prüfungen', 14, 600)}` +
        checkbox(188, 104, 'cb2') +
        `<div style="${abs(214, 98, 30, 30)}">${icon('folder', 20)}</div>${text(242, 102, 110, 'Skripte', 14, 600)}` +
        glass(
          20,
          0.55,
          'left:372px;top:16px;width:216px;height:286px;',
          orbi(12, 10, 24) + text(44, 12, 150, 'Vorschau', 13, 600) + hline(10, 42, 196),
        ) +
        bubble(426, 66, 156, 'Wann ist die Klausur?', true, 'q1').replace('box-sizing', 'opacity:0;box-sizing') +
        bubble(382, 104, 176, 'Dazu habe ich leider keine Informationen.', false, 'a1').replace('box-sizing', 'opacity:0;box-sizing') +
        bubble(426, 158, 156, 'Wann ist die Klausur?', true, 'q2').replace('box-sizing', 'opacity:0;box-sizing') +
        bubble(382, 196, 196, 'Am 14. Februar um 10 Uhr, Raum H1.', false, 'a2').replace('box-sizing', 'opacity:0;box-sizing') +
        okChip(382, 254, 196, 'Quelle: Prüfungsordnung.pdf', 'src').replace('display:flex', 'display:flex;opacity:0'),
      0,
    ),
    tracks: [
      { r: 'q1', at: [0.8, 1.2], o: [0, 1], y: [6, 0] },
      { r: 'a1', at: [2.0, 2.4, 5.0, 5.4], o: [0, 1, 1, 0.45], y: [6, 0, 0, 0] },
      { r: 'cb1', at: [4.3, 4.5], o: [0, 1] },
      { r: 'q2', at: [6.0, 6.4], o: [0, 1], y: [6, 0] },
      { r: 'a2', at: [7.6, 8.0], o: [0, 1], y: [6, 0] },
      { r: 'src', at: [8.4, 8.8], o: [0, 1], y: [4, 0] },
    ],
    cursor: {
      at: [0, 3.0, 4.0, 4.8, 14],
      x: [300, 300, 217, 260, 260],
      y: [280, 280, 91, 200, 200],
      clicks: [4.3],
    },
  });

/** 3.2 · Szene 3 — ein Ordner dient mehreren Agenten. */
const linkShared = (): HintAnimation => {
  const card = (y: number, name: string, n: number) =>
    `<div data-r="c${n}" style="opacity:0;">` +
    glass(18, 0.55, `left:350px;top:${y}px;width:230px;height:70px;`, orbi(14, 18, 34) + text(60, 14, 160, name, 14, 600) + text(60, 36, 160, 'Wissen: Prüfungen', 12, 500, C.muted)) +
    okChip(470, y + 74, 110, 'verknüpft', `k${n}`).replace('display:flex', 'display:flex;opacity:0') +
    `</div>`;
  return new StoryScene({
    seconds: 10,
    still: 8.5,
    html: panel(
      `<div style="${abs(90, 128, 60, 60, 'transform:scale(1.7);transform-origin:0 0;')}">${folderIcon()}</div>` +
        text(64, 198, 140, 'Prüfungen', 15, 600, C.text, 'text-align:center;') +
        `<svg style="position:absolute;left:0;top:0;" width="600" height="318" viewBox="0 0 600 318" fill="none" stroke="${C.accent}" stroke-width="2" stroke-dasharray="5 5">` +
        `<path data-r="l1" style="opacity:0;" d="M170 150 C 250 150, 260 92, 350 92"/>` +
        `<path data-r="l2" style="opacity:0;" d="M170 170 C 250 170, 260 236, 350 236"/></svg>` +
        card(56, 'Klausur-Coach', 1) +
        card(200, 'Statistik-Tutor', 2),
    ),
    tracks: [
      { r: 'c1', at: [0.5, 1.0], o: [0, 1], x: [16, 0] },
      { r: 'c2', at: [1.0, 1.5], o: [0, 1], x: [16, 0] },
      { r: 'l1', at: [2.5, 3.2], o: [0, 1] },
      { r: 'k1', at: [3.2, 3.5], o: [0, 1] },
      { r: 'l2', at: [4.5, 5.2], o: [0, 1] },
      { r: 'k2', at: [5.2, 5.5], o: [0, 1] },
    ],
  });
};

/** 3.3 · Szene 2 — dieselbe Frage in den drei Wissens-Modi. */
const modeCompare = (): HintAnimation => {
  const col = (x: number, n: number, mode: string, answer: string, foot: string, ok: boolean) =>
    `<div data-r="col${n}" style="opacity:0;">` +
    (n === 3 ? pressed(x - 6, 70, 182, 238, 'sel3').replace('border-radius', 'opacity:0;border-radius') : '') +
    glass(
      18,
      0.5,
      `left:${x}px;top:76px;width:170px;height:226px;`,
      text(14, 14, 142, mode, 13, 600) +
        hline(14, 40, 142) +
        `<div style="${abs(14, 52, 142, 120, `font-size:12px;line-height:1.45;color:${C.text};white-space:normal;`)}">${answer}</div>` +
        (ok ? okChip(14, 186, 142, foot) : chip(14, 186, 142, foot)),
    ) +
    `</div>`;
  return new StoryScene({
    seconds: 16,
    still: 14,
    html: panel(
      `<div style="${abs(40, 22, 520, 36, `border-radius:999px;background:rgb(255 255 255 / .55);display:flex;align-items:center;gap:10px;padding:0 16px;box-sizing:border-box;font-size:14px;font-weight:500;color:${C.text};`)}">` +
        `${icon('send', 16, C.action)}Was ist eine Normalverteilung?</div>` +
        col(40, 1, 'Uneingeschränkt', 'Eine symmetrische Glockenkurve, beschrieben durch Mittelwert und Standardabweichung — mit Beispielen aus aller Welt.', 'auch Allgemeinwissen', false) +
        col(215, 2, 'Themenspezifisch', 'Eine Glockenkurve um den Mittelwert — so erklärt, wie es zum Thema deines Kurses passt.', 'beim Kursthema', false) +
        col(390, 3, 'Nur Wissensbasis', 'Laut Skript Kapitel 3: „Die Normalverteilung ist durch μ und σ vollständig bestimmt …“', 'nur deine Quellen', true),
    ),
    tracks: [
      { r: 'col1', at: [1.5, 2.1], o: [0, 1], y: [10, 0] },
      { r: 'col2', at: [4.5, 5.1], o: [0, 1], y: [10, 0] },
      { r: 'col3', at: [7.5, 8.1], o: [0, 1], y: [10, 0] },
      { r: 'sel3', at: [11, 11.5], o: [0, 1] },
    ],
  });
};

/** 3.3 · Szene 3 — ein neuer Agent stellt sich beim ersten Wissen selbst um. */
const modeAuto = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10,
    html: panel(
      glass(
        22,
        0.55,
        'left:190px;top:70px;width:240px;height:178px;',
        orbi(18, 18, 40) +
          text(70, 22, 160, 'Neuer Agent', 15, 600) +
          text(70, 44, 160, 'Alpha Tutor', 12, 500, C.muted) +
          hline(18, 78, 204) +
          text(18, 92, 210, 'Einschränkungen der Wissensbasis', 12, 600, C.muted) +
          chip(18, 118, 150, 'Uneingeschränkt', 'open') +
          okChip(18, 118, 150, 'Nur Wissensbasis', 'strict').replace('display:flex', 'display:flex;opacity:0'),
      ) +
        `<div data-r="fly" style="${abs(40, 236, 40, 40)}">${folderIcon()}</div>`,
    ),
    tracks: [
      { r: 'fly', at: [1.5, 3.8, 4.2], x: [0, 230, 230], y: [0, -116, -116], o: [1, 1, 0] },
      { r: 'open', at: [4.6, 5.0], o: [1, 0] },
      { r: 'strict', at: [4.6, 5.0], o: [0, 1], s: [0.9, 1] },
    ],
  });

/** 3.4 · Szene 1 — Kurs-Sync in den Platzierungen einschalten und speichern. */
const syncToggle = (): HintAnimation => {
  const row = (y: number, course: string, agent: string, n: number) =>
    text(188, y + 8, 140, course, 14, 600) + text(334, y + 8, 150, agent, 13, 500, C.muted) + toggle(512, y + 9, `t${n}`) + hline(180, y + 40, 404);
  return new StoryScene({
    seconds: 12,
    still: 9.5,
    html: panel(
      text(188, 22, 140, 'Kurs', 12, 600, C.muted) +
        text(334, 22, 140, 'KI-Agent', 12, 600, C.muted) +
        text(500, 22, 84, 'Kurs-Sync', 12, 600, C.muted) +
        hline(180, 46, 404) +
        row(52, 'Statistik I', 'Statistik-Tutor', 1) +
        row(96, 'Mathematik II', 'Mathe-Tutor', 2) +
        row(140, 'Physik I', 'Klausur-Coach', 3) +
        pill(440, 252, 144, 'Speichern', 'save').replace('transform-origin', 'opacity:0;transform-origin') +
        okChip(456, 256, 112, 'Gespeichert', 'saved').replace('display:flex', 'display:flex;opacity:0'),
      0,
    ),
    tracks: [
      { r: 't1', at: [2.8, 3.0], o: [0, 1] },
      { r: 't1Knob', at: [2.8, 3.0], x: [0, 16] },
      { r: 'save', at: [3.2, 3.5, 5.5, 5.8], o: [0, 1, 1, 0] },
      { r: 'saved', at: [5.7, 6.0], o: [0, 1] },
    ],
    cursor: {
      at: [0, 2.2, 3.0, 4.6, 5.6, 12],
      x: [300, 551, 551, 532, 532, 532],
      y: [280, 99, 99, 286, 286, 286],
      clicks: [2.8, 5.3],
    },
  });
};

/** 3.4 · Szene 2 — der Kurs erscheint als Wissensquelle und liefert Antworten. */
const syncSource = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10,
    html: panel(
      text(184, 24, 300, 'Wissensbasis – Verknüpfung', 12, 600, C.muted) +
        `<div style="${abs(186, 54, 30, 30)}">${icon('folder', 20)}</div>${text(214, 58, 200, 'Prüfungen', 14, 600)}` +
        okChip(476, 58, 100, 'verknüpft') +
        hline(180, 90, 404) +
        `<div data-r="r2" style="opacity:0;">` +
        `<div style="${abs(186, 100, 30, 30)}">${icon('courses', 20)}</div>${text(214, 104, 220, 'Moodle · Statistik I', 14, 600)}` +
        chip(392, 104, 76, 'Kurs-Sync') +
        `<div data-r="trackS">${bar(490, 114, 84, 'fillS')}</div>` +
        okChip(476, 104, 100, 'Bereit', 'readyS').replace('display:flex', 'display:flex;opacity:0') +
        `</div>` +
        hline(180, 136, 404) +
        bubble(184, 160, 300, 'Wie berechne ich die Varianz?', true, 'q').replace('box-sizing', 'opacity:0;box-sizing') +
        bubble(184, 204, 380, 'Wie in Lektion 4 von Statistik I: Quadriere die Abweichungen vom Mittelwert und teile durch n.', false, 'a').replace('box-sizing', 'opacity:0;box-sizing') +
        okChip(184, 262, 220, 'Quelle: Moodle · Statistik I', 'src').replace('display:flex', 'display:flex;opacity:0'),
      0,
    ),
    tracks: [
      { r: 'r2', at: [1.2, 1.7], o: [0, 1], x: [30, 0] },
      { r: 'fillS', at: [2.0, 4.6], s: [0, 1] },
      { r: 'trackS', at: [4.6, 4.9], o: [1, 0] },
      { r: 'readyS', at: [4.6, 4.9], o: [0, 1] },
      { r: 'q', at: [6.0, 6.4], o: [0, 1], y: [6, 0] },
      { r: 'a', at: [7.4, 7.8], o: [0, 1], y: [6, 0] },
      { r: 'src', at: [8.2, 8.6], o: [0, 1] },
    ],
  });

/** 3.4 · Szene 3 — Zugriffsregeln des LMS gelten weiter. */
const syncAccess = (): HintAnimation => {
  const act = (y: number, ic: string, name: string, locked = false) =>
    `<div style="${abs(14, y, 30, 30)}">${icon(ic, 18, locked ? C.muted : C.icon)}</div>` +
    text(42, y + 4, 140, name, 13, 600, locked ? C.muted : C.text);
  return new StoryScene({
    seconds: 12,
    still: 9,
    html: panel(
      glass(
        20,
        0.5,
        'left:24px;top:20px;width:262px;height:278px;',
        text(16, 14, 200, 'Moodle · Statistik I', 13, 600, C.muted) +
          hline(14, 42, 234) +
          act(56, 'file', 'Skript Kapitel 1') +
          act(96, 'file', 'Übungsblatt 2') +
          pressed(8, 132, 246, 40, 'lockSel').replace('border-radius', 'opacity:0;border-radius') +
          act(138, 'lock', 'Klausurlösungen', true) +
          chip(178, 142, 66, 'gesperrt'),
      ) +
        glass(20, 0.55, 'left:306px;top:20px;width:270px;height:278px;', orbi(12, 10, 24) + text(44, 12, 180, 'Statistik-Tutor', 13, 600) + hline(10, 42, 250)) +
        bubble(380, 86, 186, 'Zeig mir die Klausurlösungen.', true, 'q').replace('box-sizing', 'opacity:0;box-sizing') +
        bubble(318, 140, 240, 'Darauf hast du in Moodle keinen Zugriff. Frag am besten deine Lehrperson.', false, 'a').replace('box-sizing', 'opacity:0;box-sizing'),
    ),
    tracks: [
      { r: 'q', at: [1.5, 1.9], o: [0, 1], y: [6, 0] },
      { r: 'lockSel', at: [2.4, 2.8, 6.5, 7.0], o: [0, 1, 1, 0] },
      { r: 'a', at: [3.6, 4.0], o: [0, 1], y: [6, 0] },
    ],
  });
};

/** Die Szenen des Pilots, je ID. Szenen-IDs sind fest (Katalog `video.scenes`). */
export const KNOWLEDGE_SCENES = {
  'kb-upload': kbUpload,
  'kb-folders': kbFolders,
  'link-preview': linkPreview,
  'link-shared': linkShared,
  'mode-compare': modeCompare,
  'mode-auto': modeAuto,
  'sync-toggle': syncToggle,
  'sync-source': syncSource,
  'sync-access': syncAccess,
} satisfies Record<string, () => HintAnimation>;
